/**
 * Form Comparison Modal Component
 * Displays two PDFs side by side for comparison:
 * - Left: Existing/Extracted PDF
 * - Right: Admin Form PDF (downloaded from API)
 */

import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { BASE_URL } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { fontFamily } from "../Constants/constants";
import SecureDocumentViewer from "../WorkLoade_File_Import/SecureDocumentViewer";
import UniversalDocumentViewer from "../WorkLoade_File_Import/UniversalDocumentViewer";

interface FormComparisonModalProps {
  open: boolean;
  onClose: () => void;
  existingPdfFileImportId: string; // File import ID for existing PDF
  existingPdfFileName: string; // File name for existing PDF
  formId: string; // formInstructorIdentifier or uniqueIdentifier for admin form
  existingPdfTitle?: string;
  adminFormTitle?: string;
  userId?: string;
  organizationId?: string;
}

const FormComparisonModal: React.FC<FormComparisonModalProps> = ({
  open,
  onClose,
  existingPdfFileImportId,
  existingPdfFileName,
  formId,
  existingPdfTitle = "Extracted Form",
  adminFormTitle = "Admin Form",
  userId,
  organizationId,
}) => {
  const [adminPdfData, setAdminPdfData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  /**
   * Download admin form PDF when modal opens
   */
  useEffect(() => {
    if (open && formId) {
      downloadAdminFormPdf();
    }
  }, [open, formId]);

  /**
   * Download PDF from admin forms using formId
   */
  const downloadAdminFormPdf = async () => {
    try {
      setLoading(true);
      setError("");

      const userData = getUserIdFromLocalStorage();

      // Call the DownloadFormPdf API endpoint
      const response = await fetch(`${BASE_URL}/Utilities/DownloadFormPdf?formId=${formId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userData.token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to download form PDF: ${response.status} - ${errorText}`);
      }

      // Get the content type to determine response format
      const contentType = response.headers.get("content-type");
      console.log("📦 Response Content-Type:", contentType);

      let arrayBuffer: ArrayBuffer;

      // Check if response is JSON (base64 encoded) or binary
      if (contentType?.includes("application/json")) {
        // API returns JSON with base64-encoded PDF data
        const jsonResponse = await response.json();
        console.log("📄 Received JSON response with keys:", Object.keys(jsonResponse));

        // Extract base64 data (could be in "Data" or "data" property)
        const base64Data =
          jsonResponse.Data || jsonResponse.data || jsonResponse.pdf || jsonResponse.content;

        if (!base64Data) {
          console.error("JSON response structure:", jsonResponse);
          throw new Error(
            "No base64 data found in response. Check console for response structure."
          );
        }

        console.log("✅ Extracted base64 data, length:", base64Data.length);

        // Convert base64 to ArrayBuffer
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        arrayBuffer = bytes.buffer;

        console.log("✅ Converted to ArrayBuffer, byteLength:", arrayBuffer.byteLength);
      } else {
        // Binary response
        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error("Received empty file data from server");
        }

        console.log("✅ Received binary blob, type:", blob.type, "Size:", blob.size);
        arrayBuffer = await blob.arrayBuffer();
      }

      setAdminPdfData({
        fileBytes: arrayBuffer,
        mimeType: "application/pdf",
        filename: `admin_form_${formId}.pdf`,
        extension: "pdf",
      });
    } catch (err: any) {
      console.error("Error downloading admin form PDF:", err);
      setError(err.message || "Failed to load admin form PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="form-comparison-modal">
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95vw",
          height: "95vh",
          backgroundColor: "white",
          boxShadow: "none",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: fontFamily,
              fontSize: "20px",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Form Comparison
          </h2>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Content - Two PDFs Side by Side */}
        <div
          style={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
          }}
        >
          {/* Left PDF - Existing/Extracted Form */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#f9f9f9",
                borderBottom: "1px solid #e0e0e0",
                fontFamily: fontFamily,
                fontSize: "14px",
                fontWeight: 600,
                color: "#555",
              }}
            >
              {existingPdfTitle}
            </div>
            <div style={{ flex: 1 }}>
              {/* @ts-expect-error SecureDocumentViewer is a JS component with optional props */}
              <SecureDocumentViewer
                organizationId={organizationId}
                userId={userId}
                fileImportId={existingPdfFileImportId}
                fileName={existingPdfFileName}
                style={{ height: "100%", width: "100%" }}
                fileType="uploadfiles"
              />
            </div>
          </div>

          {/* Right PDF - Admin Form */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#f9f9f9",
                borderBottom: "1px solid #e0e0e0",
                fontFamily: fontFamily,
                fontSize: "14px",
                fontWeight: 600,
                color: "#555",
              }}
            >
              {adminFormTitle}
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#fafafa",
              }}
            >
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <CircularProgress size={40} />
                  <span
                    style={{
                      fontFamily: fontFamily,
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Loading admin form...
                  </span>
                </div>
              ) : error ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px",
                  }}
                >
                  <span
                    style={{
                      fontFamily: fontFamily,
                      fontSize: "14px",
                      color: "#f44336",
                    }}
                  >
                    ❌ {error}
                  </span>
                </div>
              ) : adminPdfData?.fileBytes ? (
                <div style={{ width: "100%", height: "100%" }}>
                  {/* @ts-expect-error UniversalDocumentViewer is a JS component */}
                  <UniversalDocumentViewer
                    fileBytes={adminPdfData.fileBytes}
                    mimeType={adminPdfData.mimeType}
                    fileName={adminPdfData.filename}
                    style={{ height: "100%", width: "100%" }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FormComparisonModal;
